import { useState, useMemo, useEffect, useRef } from 'react'
import { Stack, Popover } from '@mantine/core'
import styled from 'styled-components'

import { GroupedMultiSelectProps } from './grouped-multi-select.types'

import { AccordionMultiSelect } from '../accordion-multi-select'
import { Icon } from '../icon'
import { TextInput } from '../text-input'

const StyledIconBtn = styled.button`
  ${({ theme }) =>
    `
    display: flex;
    justify-content: center;
    align-items: center;
    width: ${theme.sizes[5]};
    height: ${theme.sizes[5]};
    padding: 0;
    border: none;
    background-color: transparent;
  `}
`

const StyledSelectTrigger = styled.div`
  ${({ theme }) =>
    `
    position: relative;
    width: 100%;

    & > button {
      position: absolute;
      top: 50%;
      right: ${theme.sizes[10]};
      transform: translateY(-50%);
    }

    & > svg {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: ${theme.sizes[2]};
    }
 `}
`

const StyledFooter = styled.div`
  ${({ theme }) =>
    `
    padding: ${theme.sizes[3]} ${theme.sizes[4]};
    margin: 0;
    font-size: ${theme.fontSizes.caption};
    font-weight: ${theme.fontWeights.medium};
    background-color: ${theme.colors.gray70};
 `}
`

const StyledSearchWrapper = styled.div`
  ${({ theme }) =>
    `
    position: relative;
    padding: ${theme.sizes[3]} ${theme.sizes[4]};

    & > button {
      position: absolute;
      top: 50%;
      right: ${theme.sizes[6]};
      transform: translateY(-50%);
    }
 `}
`

export const GroupedMultiSelect = ({
  'data-testid': dataTestId = 'grouped-multi-select',
  data,
  value = [],
  onChange,
  showSelectAll = true,
  searchable = true,
  selectAllLabel = 'Select All',
  searchPlaceholder = 'Search...',
  triggerPlaceholder = 'Select items...',
  getFooterTranslation,
  withinPopup,
  borderRadiiSize,
  zIndex = 2000,
}: GroupedMultiSelectProps) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [targetWidth, setTargetWidth] = useState<number | undefined>(undefined)
  const targetRef = useRef<HTMLInputElement>(null)

  const handleChange = (newValue: string[]) => {
    onChange?.(newValue)
  }

  const filteredData = useMemo(() => {
    if (!searchTerm) return data

    const filtered: typeof data = {}
    Object.entries(data).forEach(([groupName, options]) => {
      const filteredOptions = options.filter(
        (option) =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          groupName.toLowerCase().includes(searchTerm.toLowerCase())
      )
      if (
        filteredOptions.length > 0 ||
        groupName.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        filtered[groupName] = groupName
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
          ? options
          : filteredOptions
      }
    })
    return filtered
  }, [data, searchTerm])

  // Calculate which groups should be expanded based on search
  const expandedGroups = useMemo(() => {
    if (!searchTerm) return undefined
    return Object.keys(filteredData)
  }, [searchTerm, filteredData])

  useEffect(() => {
    const updateTargetWidth = () => {
      if (targetRef.current) {
        setTargetWidth(targetRef.current.offsetWidth)
      }
    }

    updateTargetWidth()

    window.addEventListener('resize', updateTargetWidth)

    return () => {
      window.removeEventListener('resize', updateTargetWidth)
    }
  }, [])

  const getTriggerText = () => {
    if (value.length === 0) return triggerPlaceholder

    const selectedByGroup: { [groupName: string]: string[] } = {}

    Object.entries(data).forEach(([groupName, options]) => {
      const selectedInGroup = options
        .filter((option) => value.includes(option.value))
        .map((option) => option.label)

      if (selectedInGroup.length > 0) {
        selectedByGroup[groupName] = selectedInGroup
      }
    })

    const groupNames = Object.keys(selectedByGroup)
    const result: string[] = []
    let groupsDisplayedAsCount = 0

    groupNames.forEach((groupName) => {
      const selectedItems = selectedByGroup[groupName]
      const itemCount = selectedItems.length

      if (itemCount > 2) {
        result.push(`${groupName} (${itemCount})`)
        groupsDisplayedAsCount++
      } else {
        result.push(...selectedItems)
      }
    })

    if (groupsDisplayedAsCount > 3) {
      const groupCountItems = result.filter(
        (item) =>
          !groupNames.some(
            (groupName) =>
              selectedByGroup[groupName].includes(item) &&
              selectedByGroup[groupName].length <= 2
          )
      )

      const firstThreeGroups = groupCountItems.slice(0, 3)
      const remainingGroups = groupsDisplayedAsCount - 3
      firstThreeGroups.push(`(+${remainingGroups} more)`)

      const individualItems = result.filter((item) =>
        groupNames.some(
          (groupName) =>
            selectedByGroup[groupName].includes(item) &&
            selectedByGroup[groupName].length <= 2
        )
      )

      return [...firstThreeGroups, ...individualItems].join('; ')
    }

    return result.join('; ')
  }

  const renderContent = () => (
    <Stack style={{ width: '100%' }} gap={0}>
      {searchable ? (
        <StyledSearchWrapper>
          <TextInput
            name={`${dataTestId}-search`}
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.currentTarget.value)}
            data-testid={`${dataTestId}-search`}
            icon="search"
            borderRadiiSize={borderRadiiSize}
          />
          {searchTerm.length ? (
            <StyledIconBtn
              onClick={() => setSearchTerm('')}
              data-testid={`${dataTestId}-search-clear`}
            >
              <Icon name="reject" color="gray600" size={5} />
            </StyledIconBtn>
          ) : null}
        </StyledSearchWrapper>
      ) : null}

      <AccordionMultiSelect
        data-testid={dataTestId}
        data={filteredData}
        value={value}
        onChange={handleChange}
        showSelectAll={showSelectAll && !searchTerm}
        selectAllLabel={selectAllLabel}
        expandedGroups={expandedGroups}
        maxHeight="280px"
        flattenSingleItemGroups={!searchTerm}
      />

      <StyledFooter data-testid={`${dataTestId}-footer`}>
        {getFooterTranslation(value.length)}
      </StyledFooter>
    </Stack>
  )

  return (
    <div
      data-testid={dataTestId}
      style={{ width: '100%', position: 'relative' }}
    >
      <Popover
        position="bottom-start"
        radius="md"
        withinPortal={!withinPopup}
        styles={{
          dropdown: {
            zIndex,
            boxShadow: '0 8px 32px 0 rgba(13, 13, 13, 0.16)',
            border: 'none',
            padding: 0,
            overflow: 'hidden',
          },
        }}
      >
        <Popover.Target>
          <StyledSelectTrigger>
            <TextInput
              ref={targetRef}
              name={`${dataTestId}-input`}
              placeholder={searchPlaceholder}
              value={getTriggerText()}
              data-testid={`${dataTestId}-input`}
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              onChange={() => {}}
              borderRadiiSize={borderRadiiSize}
            />
            {value.length ? (
              <StyledIconBtn
                onClick={() => handleChange([])}
                data-testid={`${dataTestId}-search-clear`}
              >
                <Icon name="reject" color="gray600" size={5} />
              </StyledIconBtn>
            ) : null}
            <Icon name="dropdown-arrow-down" color="gray600" size={8} />
          </StyledSelectTrigger>
        </Popover.Target>
        <Popover.Dropdown
          style={{
            width: targetWidth ? `${targetWidth}px` : 'auto',
          }}
        >
          {renderContent()}
        </Popover.Dropdown>
      </Popover>
    </div>
  )
}
